import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import ChartWrapper from './ChartWrapper'

import { useState } from 'react'
import {
  AQUA,
  convertToPercents,
  formatNumber,
  GREEN,
  LIGHT_ORANGE,
  LIGHT_RED,
  mapCoinColor,
  ORANGE,
  PINK,
  tooltipFormatter,
  tooltipLabelFormatter,
  yaxisFormatter,
} from '../helpers'
import { useChartViewState } from '../hooks/useChartViewState'

const convertToPercentsHandler = (data) =>
  convertToPercents(data, {
    ignoreKeys: ['cumulative', 'movingAverageAll'],
    totalKey: 'all',
  })

export function VolumeChart(props) {
  const { data, loading, chartHeight, yaxisWidth } = props

  const csvFields = [
    { key: 'swap', name: 'Swap' },
    { key: 'margin', name: 'Margin trading' },
    { key: 'mint', name: 'Mint GLP' },
    { key: 'burn', name: 'Burn GLP' },
    { key: 'liquidation', name: 'Liquidation' },
    { key: 'cumulative', name: 'Cumulative' },
  ]

  const controls = {
    convertToPercents: convertToPercentsHandler,
  }

  const { viewState, togglePercentView, formattedData, ...viewSettings } =
    useChartViewState({ controls, data })

  return (
    <ChartWrapper
      title="Volume"
      loading={loading}
      csvFields={csvFields}
      data={formattedData}
      controls={controls}
      viewState={viewState}
      togglePercentView={togglePercentView}
    >
      <ResponsiveContainer width="100%" height={chartHeight}>
        <ComposedChart data={formattedData} syncId="syncA">
          <CartesianGrid strokeDasharray="10 10" />
          <XAxis
            dataKey="timestamp"
            tickFormatter={tooltipLabelFormatter}
            minTickGap={30}
          />
          <YAxis
            dataKey="all"
            interval="preserveStartEnd"
            tickCount={viewState.isPercentsView ? undefined : 7}
            tickFormatter={viewSettings.yaxisTickFormatter}
            width={yaxisWidth}
          />

          <YAxis
            dataKey="cumulative"
            orientation="right"
            yAxisId="right"
            tickFormatter={yaxisFormatter}
            width={yaxisWidth}
          />

          <Tooltip
            formatter={tooltipFormatter}
            labelFormatter={tooltipLabelFormatter}
            contentStyle={{ textAlign: 'left' }}
          />

          <Legend />

          <Bar
            unit={viewSettings.itemsUnit}
            isAnimationActive={false}
            type="monotone"
            dataKey="mint"
            stackId="a"
            name="Mint PLP"
            fill={AQUA}
          />
          <Bar
            unit={viewSettings.itemsUnit}
            isAnimationActive={false}
            type="monotone"
            dataKey="burn"
            stackId="a"
            name="Burn PLP"
            fill={ORANGE}
          />
          <Bar
            unit={viewSettings.itemsUnit}
            isAnimationActive={false}
            type="monotone"
            dataKey="swap"
            stackId="a"
            name="Swap"
            fill={PINK}
          />
          <Bar
            unit={viewSettings.itemsUnit}
            isAnimationActive={false}
            type="monotone"
            dataKey="margin"
            stackId="a"
            name="Margin trading"
            fill={GREEN}
          />
          <Bar
            unit={viewSettings.itemsUnit}
            isAnimationActive={false}
            type="monotone"
            dataKey="liquidation"
            stackId="a"
            name="Liquidation"
            fill={LIGHT_RED}
          />
          <Line
            isAnimationActive={false}
            type="monotone"
            dot={false}
            strokeWidth={3}
            dataKey="cumulative"
            yAxisId="right"
            name="Cumulative"
            stroke={LIGHT_ORANGE}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartWrapper>
  )
}

export function TokenVolumeChart({ syncId, data, loading, chartHeight }) {
  const renderCustomizedLabel = (props) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, percent, index, name } =
      props
    const RADIAN = Math.PI / 180
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5
    const x = cx + radius * Math.cos(-midAngle * RADIAN)
    const y = cy + radius * Math.sin(-midAngle * RADIAN)

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
      >
        {(percent * 100).toFixed(2)}%
      </text>
    )
  }

  // use state
  const [activeIndex, setActiveIndex] = useState(0)

  const onPieEnter = (_, index) => {
    setActiveIndex(index)
  }

  const [tabIndex, setTabIndex] = useState(1)

  const subgraphResponseKey = (_tabIndex) => {
    if (_tabIndex === 0) {
      return 'daily'
    }
    if (_tabIndex === 1) {
      return 'weekly'
    }
    if (_tabIndex === 2) {
      return 'monthly'
    }
    if (_tabIndex === 3) {
      return 'total'
    }
    throw new Error(`not support TabIndex`)
  }

  const onChangeTabs = (index) => {
    setTabIndex(index)
  }

  const CustomTooltip = (props) => {
    const { active, payload } = props

    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-2 rounded-lg p-4 h-full w-full text-base text-right">
          <p className={`text-[${mapCoinColor.get(payload[0].name)}]`}>
            {payload[0].name} (
            {
              // payload comes from library, other value from name and value should get in payload[0].payload.payload.field
              (
                (payload[0].value / payload[0].payload.payload.totalVolume) *
                100
              ).toFixed(2)
            }
            %)
          </p>
          <p className="text-gray-6">
            Trading Volume: ${formatNumber(payload[0].value)}
          </p>
        </div>
      )
    }

    return null
  }

  return (
    <ChartWrapper
      title="Trade Volume By Assets"
      loading={loading}
      data={data && data[subgraphResponseKey(tabIndex)]}
      csvFields={[
        { key: 'start' },
        { key: 'end' },
        { key: 'name' },
        { key: 'value' },
      ]}
      csvStartTimestampField={'start'}
      csvEndTimestampField={'end'}
      toggleTabView={true}
      // tabs
      onChangeTabs={onChangeTabs}
      currentTabIndex={tabIndex}
      tabHeaderValues={[
        'Last 24 hrs',
        'Last 7 days ',
        'Last 30 days',
        'All Time',
      ]}
    >
      {data && data[subgraphResponseKey(tabIndex)].length > 0 ? (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <PieChart>
            <Pie
              activeIndex={activeIndex}
              data={data && data[subgraphResponseKey(tabIndex)]}
              labelLine={false}
              label={renderCustomizedLabel}
              onMouseEnter={onPieEnter}
              outerRadius={120}
              fill="#8884d8"
              dataKey="value"
            >
              {data &&
                data[subgraphResponseKey(tabIndex)].map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={mapCoinColor.get(entry.name)}
                  />
                ))}
            </Pie>
            <Tooltip
              content={<CustomTooltip />}
              wrapperStyle={{ outline: 'none' }}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-100 md:h-full w-full flex justify-center items-center">
          No Data
        </div>
      )}
    </ChartWrapper>
  )
}

export default {
  VolumeChart,
  TokenVolumeChart,
}
